import React, { PropsWithChildren } from 'react'
import styled from 'styled-components'

import { ShareListItemFragment } from '@sketch/gql-types'
import {
  LoadingPlaceholder,
  TimeAgo,
  timeUnits,
  TableComponents,
  HighlightedText,
  TimeAgoProps,
  Tooltip,
  DeletingIn,
} from '@sketch/components'
import DocumentCardIcons, {
  DesignSystemForLibraryList,
  LibraryIcon,
  ArchivedIcon,
  PinnedIcon,
  sharedStatusToIconMap,
  TemplateIcon,
} from '../../../../components/DocumentCard/Icons/DocumentCardIcons'

import DocumentPresenceCreatorAvatars, {
  AvatarItem,
} from '../DocumentPresenceCreatorAvatars'

import { ReactComponent as CloudUpload } from '@sketch/icons/cloud-arrow-up-64'
import { ReactComponent as DocumentProcessing } from '@sketch/icons/document-placeholder-64'
import { ReactComponent as WarningTriangle } from '@sketch/icons/warning-triangle-64'
import { ReactComponent as ImagePlaceholder } from '@sketch/icons/image-64'

import {
  DragAndDropEvents,
  RenderStatus,
  useFlag,
} from '@sketch/modules-common'
import { DocumentSharingStatus } from '../../utils/getDocumentSharingStatus'
import { ShareType } from '../EditInSketchButton'
import { useSearch, intersperse } from '@sketch/utils'

import {
  TooltipContent,
  TooltipItem,
  IconContainer,
} from '../HeaderNavigation/ProjectsDropdown.styles'
import { ProjectIcon } from '../../../projects/components/ProjectIcon'
import { useGetParentProjects } from '../../../projects/operations'

import {
  ImageWrapper,
  ImageElement,
  GridWrapper,
  Name,
  Image,
  InfoIcon,
  StatusIcon,
  AutosaveWrapper,
  SubtitleTextWrapper,
  TableCell,
  DropdownButtonWrapper,
  NameWrapper,
  TableAvatarNames,
  GridPlaceholderWrapper,
  TablePlaceholderWrapper,
  TopLeftIcons,
  BottomRightIcons,
  IconsWrapper,
  TooltipStyled,
  DroppableTableWrapper,
  SelectedCheckbox,
  SelectedCheckboxWrapper,
  SelectedShadowWrapper,
  SelectedCheckboxFloatingWrapper,
  SelectedShadow,
} from './DocumentItem.styles'

export interface DocumentItemProps {
  className?: string
  identifier: string
  workspaceId: string
  name: string
  renderStatus: RenderStatus
  thumbnailSource?: string
  lastUpdatedDate: string
  isLatestVersionAutosaved?: boolean
  documentSharingStatus: DocumentSharingStatus
  project?: ShareListItemFragment['project']
  collectionName?: string
  deletedAt?: string | null
  isEditing?: boolean
  type?: ShareType
  onClick?: React.MouseEventHandler<HTMLElement>
  avatars: AvatarItem[]
  renderDropdown?: () => React.ReactNode
  designSystems: DesignSystemForLibraryList[]
  archivedAt?: string | null
  pinned?: string | null
  dragAndDropEvents?: DragAndDropEvents
  droppable?: boolean
  selected?: boolean
  onSelectChange?: (selected: boolean) => void

  /* This property only applies to the grid presentation */
  showSelectionCheckbox?: boolean
}

export const handleTimeFormat: TimeAgoProps['formatter'] = (value, unit) => {
  if (unit === 'second') return 'Now'

  return `${value}${timeUnits[unit]} ago`
}

export const handleDeletedAtTimeFormat: TimeAgoProps['formatter'] = (
  value,
  unit
) => {
  if (unit === 'second') return '(Deleted now)'

  return `(Deleted ${value}${timeUnits[unit]} ago)`
}

export const renderImage = (
  props: Pick<DocumentItemProps, 'name' | 'renderStatus' | 'thumbnailSource'>,
  layout: 'grid' | 'list'
) => {
  const { name, renderStatus, thumbnailSource } = props

  let image
  let tooltipMessage = name

  switch (renderStatus) {
    case 'failed':
      tooltipMessage = `"${name}" - Processing failed.`

      image = (
        <StatusIcon as={WarningTriangle} aria-label="Processing Failed icon" />
      )
      break

    case 'loading':
    case 'uploading':
      tooltipMessage = `"${name}" - Uploading document…`

      image = (
        <StatusIcon as={CloudUpload} aria-label="Uploading to cloud icon" />
      )
      break

    case 'ready': {
      if (!thumbnailSource) {
        image = (
          <StatusIcon as={ImagePlaceholder} aria-label="No Image available" />
        )
      } else {
        image = (
          <Image
            alt={name}
            customImageElement={ImageElement}
            src={thumbnailSource}
            loadingPlaceholder={
              layout === 'grid' ? (
                <LoadingPlaceholder size="64px" />
              ) : (
                <LoadingPlaceholder size="17px" />
              )
            }
          />
        )
      }

      break
    }

    case 'processing':
      tooltipMessage = `"${name}" - Processing document…`

      image = (
        <StatusIcon as={DocumentProcessing} aria-label="Processing icon" />
      )
      break
  }

  return { image, tooltipMessage }
}

const getButtonProps = (
  handleOnClick: (event: React.MouseEvent<HTMLElement>) => void
) => ({
  role: 'button',
  tabIndex: 0,
  onClick: handleOnClick,
  onKeyPress: (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      /* Converge for the same type of event */
      handleOnClick(event as unknown as React.MouseEvent<HTMLElement>)
    }
  },
})

export const DocumentGridItem = styled(function DocumentGridItem(
  props: DocumentItemProps
) {
  const {
    className,
    workspaceId,
    name,
    documentSharingStatus,
    project,
    collectionName,
    lastUpdatedDate,
    isLatestVersionAutosaved,
    isEditing,
    deletedAt,
    renderDropdown,
    onClick,
    avatars,
    designSystems,
    type,
    archivedAt,
    pinned,
    selected,
    onSelectChange,
    showSelectionCheckbox,
    identifier,
  } = props

  const isDesignSystemsOn = useFlag('design-systems')
  const isNestedProjectsOn = useFlag('nested-projects')
  const isMultipleSelectionOn = useFlag('multiple-selection')

  const { image, tooltipMessage } = renderImage(props, 'grid')
  const dropdownContent = renderDropdown?.()
  const a11yProps = onClick ? getButtonProps(onClick) : {}

  const { search } = useSearch()

  const showDesignSystems = !!(isDesignSystemsOn && designSystems?.length)
  const isLibrary = type === 'LIBRARY'
  const isTemplate = type === 'TEMPLATE'
  const hasTopRightIcons = !!pinned

  const sharingStatusIcon = sharedStatusToIconMap[documentSharingStatus]

  const hasBottomRightIcons =
    isLibrary || isTemplate || !!sharingStatusIcon || (!!archivedAt && !!search)

  return (
    <GridWrapper
      className={className}
      aria-label={name}
      $isSelected={selected}
      $showSelectionCheckbox={showSelectionCheckbox}
      {...a11yProps}
    >
      <IconsWrapper>
        {hasTopRightIcons && (
          <TopLeftIcons>
            <PinnedIcon />
          </TopLeftIcons>
        )}
        <ImageWrapper data-share-id={identifier} title={tooltipMessage}>
          {image}

          {isMultipleSelectionOn && (
            <SelectedCheckboxFloatingWrapper
              onClick={event => event.stopPropagation()}
            >
              <span className="sr-only">
                {selected ? 'Unselect document' : 'Select document'}
              </span>
              <SelectedCheckbox
                type="checkbox"
                checked={selected}
                onChange={event => onSelectChange?.(event.target.checked)}
              />
            </SelectedCheckboxFloatingWrapper>
          )}
        </ImageWrapper>

        {selected && (
          <SelectedShadowWrapper>
            <SelectedShadow />
          </SelectedShadowWrapper>
        )}

        {hasBottomRightIcons && (
          <BottomRightIcons>
            {archivedAt && search && <ArchivedIcon />}
            {sharingStatusIcon}
            {isLibrary && (
              <LibraryIcon
                showDesignSystems={showDesignSystems}
                designSystems={designSystems}
              />
            )}
            {isTemplate && <TemplateIcon />}
          </BottomRightIcons>
        )}
      </IconsWrapper>
      <Name>
        <HighlightedText search={search}>{name}</HighlightedText>
      </Name>
      <SubtitleTextWrapper>
        {project?.identifier && (
          <ProjectHierarchyTooltip
            workspaceId={workspaceId}
            projectId={project.identifier}
            parentProjectId={project.parentProjectIdentifier}
          >
            <b className="shrink">
              <HighlightedText search={search}>{project.name}</HighlightedText>
            </b>
          </ProjectHierarchyTooltip>
        )}
        {collectionName && !isNestedProjectsOn && (
          <b className="shrink">
            <HighlightedText search={search}>{collectionName}</HighlightedText>
          </b>
        )}
        <span className="keep-size">
          {isEditing ? (
            'Active'
          ) : (
            <TimeAgo date={lastUpdatedDate} formatter={handleTimeFormat} />
          )}
        </span>
        {(!isLatestVersionAutosaved ||
          (isLatestVersionAutosaved && isEditing)) && (
          <DocumentPresenceCreatorAvatars
            avatars={avatars}
            isEditing={isEditing || false}
            showTooltipText
          />
        )}
      </SubtitleTextWrapper>

      {deletedAt && (
        <SubtitleTextWrapper>
          <DeletingIn deletedAt={deletedAt} />
        </SubtitleTextWrapper>
      )}

      {archivedAt && !deletedAt && (
        <SubtitleTextWrapper>Archived</SubtitleTextWrapper>
      )}

      {dropdownContent && (
        <DropdownButtonWrapper aria-label="Document Options">
          {dropdownContent}
        </DropdownButtonWrapper>
      )}
    </GridWrapper>
  )
})``

export const DocumentListItem = styled(function DocumentListItem(
  props: DocumentItemProps
) {
  const {
    className,
    workspaceId,
    name,
    documentSharingStatus,
    project,
    collectionName,
    isEditing,
    type,
    lastUpdatedDate,
    isLatestVersionAutosaved,
    deletedAt,
    renderDropdown,
    onClick,
    avatars,
    designSystems,
    pinned,
    dragAndDropEvents,
    droppable = true,
    selected,
    onSelectChange,
    identifier,
  } = props

  const isMultipleSelectionOn = useFlag('multiple-selection')

  const { image, tooltipMessage } = renderImage(props, 'list')
  const dropdownContent = renderDropdown?.()

  const a11yProps = onClick ? getButtonProps(onClick) : {}

  const { search } = useSearch()
  const isDroppable =
    droppable &&
    Boolean(
      dragAndDropEvents?.isDroppableAreaEnabled &&
        dragAndDropEvents?.isDraggedOver &&
        !dragAndDropEvents?.isDraggedOverItself
    )

  return (
    <DroppableTableWrapper
      className={className}
      aria-label={name}
      data-testid="drag-wrapper"
      data-share-id={identifier}
      $isDraggedOver={isDroppable}
      $isSelected={selected}
      {...dragAndDropEvents}
      {...a11yProps}
    >
      <TableCell>
        <NameWrapper data-drag-image>
          {isMultipleSelectionOn && (
            <SelectedCheckboxWrapper onClick={event => event.stopPropagation()}>
              <span className="sr-only">
                {selected ? 'Unselect document' : 'Select document'}
              </span>
              <SelectedCheckbox
                type="checkbox"
                checked={selected}
                onChange={event => onSelectChange?.(event.target.checked)}
              />
            </SelectedCheckboxWrapper>
          )}
          <ImageWrapper title={tooltipMessage}>{image}</ImageWrapper>
          <Name>
            <HighlightedText search={search}>{name}</HighlightedText>
            <DocumentCardIcons
              documentSharingStatus={documentSharingStatus}
              designSystems={designSystems}
              type={type}
              pinned={pinned}
            />
          </Name>
        </NameWrapper>
      </TableCell>
      <TableCell className="wrap">
        <span>
          {project?.identifier ? (
            <ProjectHierarchyTooltip
              workspaceId={workspaceId}
              projectId={project.identifier}
              parentProjectId={project.parentProjectIdentifier}
            >
              <HighlightedText search={search}>{project.name}</HighlightedText>
            </ProjectHierarchyTooltip>
          ) : (
            '—'
          )}
        </span>
      </TableCell>
      {!collectionName ? null : (
        <TableCell className="wrap">
          <span>
            <HighlightedText search={search}>{collectionName}</HighlightedText>
          </span>
        </TableCell>
      )}
      {deletedAt ? (
        <TableCell>
          <TimeAgo date={deletedAt} formatter={handleTimeFormat} />
        </TableCell>
      ) : (
        <TableCell>
          <span>
            {isEditing ? (
              'Active'
            ) : (
              /* TODO: https://github.com/sketch-hq/Cloud/issues/9044 */
              /* The time format needs to be adjusted to show date in a format "Yesterday at 5:32pm" */
              <TimeAgo date={lastUpdatedDate} formatter={handleTimeFormat} />
            )}
          </span>
        </TableCell>
      )}
      <TableCell>
        {!isEditing && isLatestVersionAutosaved ? (
          <AutosaveWrapper>
            Autosave
            <Tooltip
              placement="bottom"
              content="Autosaved versions include the document's latest changes"
            >
              <InfoIcon role="img" />
            </Tooltip>
          </AutosaveWrapper>
        ) : (
          <NameWrapper type="table">
            <DocumentPresenceCreatorAvatars
              avatars={avatars}
              isEditing={isEditing || false}
              showTooltipText
            />
            <TableAvatarNames>
              {intersperse(
                avatars.map(({ displayName, isMe }, index) => (
                  <HighlightedText key={index} search={isMe ? '' : search}>
                    {displayName}
                  </HighlightedText>
                )),
                ', '
              )}
            </TableAvatarNames>
          </NameWrapper>
        )}
      </TableCell>
      <TableComponents.TableCellSticky>
        {dropdownContent}
      </TableComponents.TableCellSticky>
    </DroppableTableWrapper>
  )
})``

interface PlaceholderProps {
  inViewListener?: React.ReactNode
  columnCount?: number
}

export const DocumentGridItemPlaceholder: React.FCC<
  PlaceholderProps
> = props => {
  return (
    <GridPlaceholderWrapper>
      <ImageWrapper />
      {props.inViewListener}
    </GridPlaceholderWrapper>
  )
}

export const DocumentTableItemPlaceholder: React.FCC<PlaceholderProps> = ({
  columnCount = 5,
  inViewListener,
}) => (
  <TablePlaceholderWrapper>
    <TableCell>
      <ImageWrapper />
      {inViewListener}
    </TableCell>

    {/* Placeholder cells to make sure the table is correctly rendered */}
    {Array.from({ length: columnCount - 1 }, (_, i) => (
      <TableCell key={i} />
    ))}
  </TablePlaceholderWrapper>
)

interface ProjectHierarchyTooltipProps extends PropsWithChildren<{}> {
  projectId: string
  workspaceId: string
  parentProjectId: string | null
}

/**
 * Tooltip that shows the project hierarchy for a document
 */
function ProjectHierarchyTooltip(props: ProjectHierarchyTooltipProps) {
  const { parentProjectId, children } = props
  return (
    <TooltipStyled
      disabled={parentProjectId === null}
      placement="bottom-start"
      content={<ProjectHierarchyTooltipContent {...props} />}
    >
      {children}
    </TooltipStyled>
  )
}

function ProjectHierarchyTooltipContent({
  workspaceId,
  projectId,
}: ProjectHierarchyTooltipProps) {
  const parentProjects = useGetParentProjects(workspaceId, projectId)

  return parentProjects.length < 2 ? null : (
    <TooltipContent>
      {parentProjects.map((project, index) => (
        <TooltipItem depth={index} key={project.identifier}>
          <IconContainer>
            <ProjectIcon project={project} isOpen={false} />
          </IconContainer>
          {project.name}
        </TooltipItem>
      ))}
    </TooltipContent>
  )
}
